import { useEffect, useRef, useState } from "react";
import { useAvatarSession } from "./useAvatarSession";
import VideoPlayer from "./VideoPlayer";
import SessionControls from "./SessionControls";
import StartButtons from "./StartButtons";
import KnowledgeReferences from "./KnowledgeReferences";
import { useToast } from "../../../hooks/useToast";

interface Avatar {
  id: string;
  name: string;
  liveavatarAvatarId: string;
  elevenlabsVoiceId: string;
}

interface ExpiringLink {
  id: string;
  slug: string;
  expiresAt: string;
  expired?: boolean;
}

interface KnowledgeReference {
  query: string;
  references: Array<{
    answerId: string;
    relevanceScore: number;
  }>;
  timestamp: number;
}

interface StreamingAvatarChatProps {
  avatar: Avatar;
  expiringLink?: ExpiringLink;
  knowledgeBase?: string;
  previewUrl?: string;
}

export default function StreamingAvatarChat({
  avatar,
  expiringLink,
  knowledgeBase,
  previewUrl,
}: StreamingAvatarChatProps) {
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [stream, setStream] = useState<MediaStream>();
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isKnowledgeDrawerOpen, setIsKnowledgeDrawerOpen] = useState(false);
  const [knowledgeReferences, setKnowledgeReferences] = useState<
    KnowledgeReference[]
  >([]);
  const [showButton, setShowButton] = useState(false);

  const videoContainerRef = useRef<HTMLDivElement>(null);
  const { error: showErrorToast } = useToast();
  const hasKnowledgeReferences = knowledgeReferences.length > 0;

  const avatarSession = useAvatarSession({
    onStreamReady: (videoStream) => {
      setStream(videoStream);
      setIsLoadingSession(false);
    },
    onAudioReady: (audioElement) => {
      // Audio element is managed by the hook
    },
    onKnowledgeReferences: (reference) => {
      setKnowledgeReferences((prev) => [...prev, reference]);
      setShowButton(true);
    },
    onError: (message) => {
      showErrorToast(message);
    },
    onDisconnect: () => {
      if (isSessionActive) {
        showErrorToast("Connection lost. Please try again.");
      }
      handleEndSession();
    },
  });

  async function fetchSessionData() {
    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      // Use guest endpoint if expiringLink is provided
      const endpoint = expiringLink
        ? `/api/${expiringLink.slug}/streaming_token`
        : `/api/avatars/${avatar.id}/streaming_token`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch session data");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching session data:", error);
      return null;
    }
  }

  async function startSession(enableVideo: boolean = true) {
    setIsLoadingSession(true);
    setIsVideoEnabled(enableVideo);

    const sessionData = await fetchSessionData();
    if (!sessionData) {
      setIsLoadingSession(false);
      showErrorToast("Failed to start session. Please try again.");
      return;
    }

    try {
      await avatarSession.connect(sessionData);
      setIsSessionActive(true);
    } catch (error) {
      console.error("Error starting avatar session:", error);
      setIsLoadingSession(false);
      showErrorToast(
        error instanceof Error
          ? error.message
          : "Failed to start session. Please try again."
      );
    }
  }

  async function handleEndSession() {
    // Exit fullscreen if active
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }

    await avatarSession.disconnect();
    setStream(undefined);
    setIsSessionActive(false);
    setIsVideoEnabled(true);
    setKnowledgeReferences([]);
  }

  useEffect(() => {
    // Handle fullscreen change events (e.g., user presses ESC)
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      handleEndSession();
    };
  }, []);

  useEffect(() => {
    if (!hasKnowledgeReferences) {
      setIsKnowledgeDrawerOpen(false);
    }
  }, [hasKnowledgeReferences]);

  return (
    <div className="drawer drawer-end">
      <input
        id="knowledge-references-drawer"
        type="checkbox"
        className="drawer-toggle"
        checked={isKnowledgeDrawerOpen}
        onChange={(event) => setIsKnowledgeDrawerOpen(event.target.checked)}
      />
      <div className="drawer-content">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">Talk to {avatar.name}</h1>
          </div>

          {/* Video Stream */}
          <VideoPlayer
            stream={stream}
            isLoading={isLoadingSession}
            isVideoEnabled={isVideoEnabled}
            previewUrl={previewUrl}
            avatarName={avatar.name}
            isFullscreen={isFullscreen}
            videoContainerRef={videoContainerRef}
          >
            {/* Floating Controls */}
            {isSessionActive && (
              <SessionControls
                room={avatarSession.room}
                isVideoEnabled={isVideoEnabled}
                onEndSession={handleEndSession}
              />
            )}
          </VideoPlayer>

          {/* Start Buttons */}
          {!isSessionActive && (
            <StartButtons
              onStartVideo={() => startSession(true)}
              onStartVoice={() => startSession(false)}
              isLoading={isLoadingSession}
            />
          )}
        </div>

        {hasKnowledgeReferences && (
          <button
            type="button"
            className={`btn btn-primary btn-lg shadow-2xl fixed bottom-6 right-6 z-30 gap-2 transition-all duration-500 ${
              showButton ? "animate-pulse" : ""
            }`}
            onClick={() => {
              setIsKnowledgeDrawerOpen(true);
              setShowButton(false);
            }}
            style={{
              animation: showButton
                ? "fadeIn 0.8s ease-in-out, pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
                : "none",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
              />
            </svg>
            Knowledge
          </button>
        )}
      </div>
      <div className="drawer-side z-40">
        <button
          type="button"
          className="drawer-overlay"
          aria-label="Close Q&A panel"
          onClick={() => setIsKnowledgeDrawerOpen(false)}
        />
        <aside className="w-96 max-w-[85vw] bg-base-100 p-6 shadow-2xl overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Knowledge Base</h2>
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-circle"
              onClick={() => setIsKnowledgeDrawerOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <KnowledgeReferences references={knowledgeReferences} />
        </aside>
      </div>
    </div>
  );
}
